import { useContext, useEffect, useState } from 'react';
import type { Vector3 } from 'three'
import { CAMERA_LIGHT_DISTANCE } from '../Camera/settings'
import { useThree } from '@react-three/fiber'
import {
    DEFAULT_MODEL_VIEWER_CYCLORAMA_BOUNCE_STRENGTH,
    DEFAULT_MODEL_VIEWER_CYCLORAMA_COLOR,
    DEFAULT_MODEL_VIEWER_CYCLORAMA_REFLECTION_STRENGTH,
    DEFAULT_MODEL_VIEWER_PRESENTATION_SETUP,
    DEFAULT_MODEL_VIEWER_STUDIO_AMBIENT_LIGHT_INTENSITY,
    DEFAULT_MODEL_VIEWER_STUDIO_DIRECT_LIGHT_INTENSITY,
    type ModelViewerPresentationSetup,
} from './Setup';
import StudioLights from './Studio/Lights';
import RenderingContext from '../Scene/RenderingContext';
import { getNeutralLightPosition } from './getNeutralLightPosition';

export interface LightsProps {
    cameraDirection: Vector3
    cycloramaBounceStrength?: number
    cycloramaColor?: string
    cycloramaReflectionStrength?: number
    directionalShadows?: boolean
    lightDistance?: number
    presentationSetup?: ModelViewerPresentationSetup
    studioAmbientLightIntensity?: number
    studioDirectLightIntensity?: number
}

export default function Lights(props: LightsProps) {
    const {
        cameraDirection,
        lightDistance = CAMERA_LIGHT_DISTANCE
    } = props
    const presentationSetup = props.presentationSetup ?? DEFAULT_MODEL_VIEWER_PRESENTATION_SETUP

    const camera = useThree((state) => state.camera)
    const { fabricLighting = false } = useContext(RenderingContext)

    const getLightPosition = () => {
        return getNeutralLightPosition(camera, cameraDirection, lightDistance, fabricLighting)
    }

    const [lightPosition, setLightPosition] = useState(getLightPosition())

    useEffect(() => {
        setLightPosition(getLightPosition())
    }, [cameraDirection, camera.position, lightDistance, fabricLighting])

    if(presentationSetup === 'studio') {
        return <StudioLights
            ambientLightIntensity={
                props.studioAmbientLightIntensity
                ?? DEFAULT_MODEL_VIEWER_STUDIO_AMBIENT_LIGHT_INTENSITY
            }
            bounceStrength={
                props.cycloramaBounceStrength
                ?? DEFAULT_MODEL_VIEWER_CYCLORAMA_BOUNCE_STRENGTH
            }
            cycloramaColor={
                props.cycloramaColor ?? DEFAULT_MODEL_VIEWER_CYCLORAMA_COLOR
            }
            directLightIntensity={
                props.studioDirectLightIntensity
                ?? DEFAULT_MODEL_VIEWER_STUDIO_DIRECT_LIGHT_INTENSITY
            }
            reflectionStrength={
                props.cycloramaReflectionStrength
                ?? DEFAULT_MODEL_VIEWER_CYCLORAMA_REFLECTION_STRENGTH
            }
        />;
    }

    return <>
        <directionalLight color={0xffffff} intensity={fabricLighting ? 1.35 : 1.0} position={lightPosition} castShadow={props.directionalShadows} shadow-bias={-0.00002} shadow-radius={2} shadow-mapSize={[2048, 2048]} />
    </>
}
